import { useTranslations } from '@vocab/react';
import {
  Dialog,
  Text,
  Box,
  Button,
  Textarea,
  Inline,
  Autosuggest,
  filterSuggestions,
  useToast,
  Divider,
} from 'braid-design-system';
import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import CreateThreadButton from 'src/components/atoms/CreateThread/CreateThreadButton/CreateThreadButton';
import CreateThreadTooltip from 'src/components/atoms/CreateThread/CreateThreadTooltip/CreateThreadTooltip';
import ThreadPersonalDetails from 'src/components/atoms/ThreadPersonalDetails/ThreadPersonalDetails';
import { CONTENT_CHARACTER_LIMIT } from 'src/core/domain/constants';
import {
  ContentValidationError,
  ThreadError,
  validateContent,
  validateThread,
} from 'src/core/domain/errors';
import errorTranslations from 'src/core/domain/errors/.vocab';
import { handleAuthCheck } from 'src/core/utils/authUtil';
import { convertThreadToSlug, slugSubstring } from 'src/core/utils/urlUtil';
import { NAV_PATHS } from 'src/paths';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useConfig } from 'src/provider/config';
import { useGroup } from 'src/provider/group';
import { useLoginModal } from 'src/provider/loginModal';
import { useProfile } from 'src/provider/profile';
import {
  createThread,
  type CreateThreadParams,
} from 'src/services/threadsService';

import translations from './.vocab';

import * as styles from './CreateThreadCard.css';

interface CreateThreadModalProps {
  handleCreateThreadAnalytics: () => void;
}

export default ({ handleCreateThreadAnalytics }: CreateThreadModalProps) => {
  const showToast = useToast();
  const navigate = useNavigate();
  const config = useConfig();
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);
  const { trackAnalyticsInteraction } = useAnalytics();
  const { authStatus } = useAuthStatus();
  const { openLoginDialog } = useLoginModal();

  const [selectedCategory, setSelectedCategory] = useState({
    text: '',
    value: '',
  });
  const [content, setContent] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [isNext, setIsNext] = useState(false);
  const [discardState, setDiscardState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postLoading, setPostLoading] = useState(false);
  const autoSuggestRef = useRef<HTMLInputElement>(null);

  const {
    state: { author },
  } = useProfile();
  const groups = useGroup().groupState.category;
  const suggestions =
    groups.map((option: any) => ({
      text: option.name,
      value: option.categoryId,
    })) || [];

  const handleCreateButtonClick = async () => {
    handleCreateThreadAnalytics();
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    setModalOpen(!modalOpen);
  };

  const handlePost = async () => {
    try {
      validateThread(content, selectedCategory.value);
      setPostLoading(true);
      const params: CreateThreadParams = {
        description: content,
        categoryId: selectedCategory.value,
        config,
      };
      const result = await createThread(params);
      if (result.__typename === 'CreateSeekMaxThreadSuccess') {
        const threadId = result.thread.id;
        const threadTitle = content ? slugSubstring(content) : '';
        const slug = convertThreadToSlug(threadTitle);
        const threadSlug = slug !== '' ? `${slug}-${threadId}` : threadId;
        trackAnalyticsInteraction('PostThread', {
          threadId,
          learningCategory: selectedCategory.text,
        });
        showToast({
          message: t('threadPosted'),
          tone: 'positive',
          action: {
            label: 'View thread',
            onClick: () => {
              const url = NAV_PATHS.communityThreadDetails.replace(
                ':threadSlug',
                threadSlug,
              );
              navigate(url);
            },
          },
        });
        // onPostThread(page, threadId, selectedCategory.text);
        setIsNext(false);
        setModalOpen(false);
        setPostLoading(false);
      } else {
        throw new ThreadError();
      }
    } catch (err) {
      const errorToDisplay =
        err instanceof ContentValidationError || err instanceof ThreadError
          ? err
          : new ThreadError();
      setError(errorTranslation(errorToDisplay.key));
      setPostLoading(false);
    }
  };

  const handleNextSubmit = async () => {
    try {
      validateContent(content);
      setIsNext(true);
    } catch (err) {
      const errorToDisplay =
        err instanceof ContentValidationError || err instanceof ThreadError
          ? err
          : new ThreadError();
      setError(errorTranslation(errorToDisplay.key));
    }
  };

  const dismissError = () => {
    setError(null);
  };

  const handleCancel = () => {
    if (content) {
      setDiscardState(true);
    } else {
      setModalOpen(false);
      setError(null);
      setIsNext(false);
    }
  };

  const confirmDiscardThread = () => {
    setDiscardState(false);
    trackAnalyticsInteraction('DiscardPostThread', {
      learningCategory: selectedCategory.text,
      callToAction: 'discard',
      nudgeType: 'discard_thread',
    });
    setModalOpen(false);
    setError(null);
    setContent('');
    setIsNext(false);
    setSelectedCategory({ text: '', value: '' });
  };

  const handleCloseCancel = () => {
    setDiscardState(false);
    trackAnalyticsInteraction('DiscardPostThread', {
      learningCategory: selectedCategory.text,
      callToAction: 'cancel',
      nudgeType: 'discard_thread',
    });
  };

  return (
    <>
      <CreateThreadButton onClick={handleCreateButtonClick} />
      <Box className={styles.mobileCommentText}>
        {discardState && (
          <Dialog
            id="discard-thread-commentTextArea"
            title={t('discardThreadTitle')}
            description={<Text>{t('discardThreadDescription')}</Text>}
            open={modalOpen}
            onClose={confirmDiscardThread}
            width="small"
            closeLabel={t('discard')}
          >
            <Inline space="small" align="left" collapseBelow="tablet">
              <Button
                variant="ghost"
                tone="critical"
                onClick={confirmDiscardThread}
              >
                {t('discard')}
              </Button>
              <Button
                variant="transparent"
                tone="formAccent"
                onClick={handleCloseCancel}
              >
                {t('cancel')}
              </Button>
            </Inline>
          </Dialog>
        )}
        {!discardState && (
          <Dialog
            id="discard-thread-commentTextArea"
            title={t('threadCreate')}
            open={modalOpen}
            onClose={handleCancel}
            width="small"
            closeLabel="Cancel"
          >
            <ThreadPersonalDetails author={author} postingDate="Now" />
            <Textarea
              id="create-thread-content"
              aria-label="Create a thread"
              value={content}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                setContent(event.target.value)
              }
              placeholder={t('shareCommunity')}
              characterLimit={CONTENT_CHARACTER_LIMIT}
              tone={error ? 'critical' : 'neutral'}
              message={!isNext && error ? error : ''}
            />
            {isNext && (
              <Autosuggest
                id="preset"
                label={t('selectLocation')}
                placeholder={t('placeholderSelectGroup')}
                ref={autoSuggestRef}
                suggestions={filterSuggestions(suggestions)}
                onChange={({ text, value }) =>
                  setSelectedCategory({
                    text,
                    value: value!,
                  })
                }
                value={selectedCategory}
                tone={error ? 'critical' : 'neutral'}
                message={error ? error : ''}
              />
            )}
            <Box
              display="flex"
              justifyContent="spaceBetween"
              alignItems="center"
            >
              <Inline space="small" align="left">
                {error ? (
                  <Button
                    variant="ghost"
                    tone="critical"
                    onClick={dismissError}
                  >
                    OK
                  </Button>
                ) : (
                  <>
                    {isNext ? (
                      <Button
                        variant={postLoading ? 'transparent' : 'ghost'}
                        tone="formAccent"
                        onClick={handlePost}
                        loading={postLoading}
                      >
                        {t('post')}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        tone="formAccent"
                        onClick={handleNextSubmit}
                      >
                        {t('next')}
                      </Button>
                    )}
                  </>
                )}
              </Inline>
              {content.length < CONTENT_CHARACTER_LIMIT - 150 && (
                <Text tone="secondary">
                  {content.length}/{CONTENT_CHARACTER_LIMIT}
                </Text>
              )}
            </Box>
            <Box display="flex" alignItems="flexStart">
              <CreateThreadTooltip />
            </Box>
          </Dialog>
        )}
      </Box>
      <Divider />
    </>
  );
};
